/**
 * Flower painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const flowerPaintingIdeasPack: ToilePack = {
    slug: 'flower-painting-ideas',
    category: 'craft',
    name: 'Flower painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'pot', label: 'Painted pot', subject: 'a terracotta flower pot painted with white daisies and dots, a succulent planted in it'},
        {key: 'vase', label: 'Painted vase', subject: 'a clear glass vase painted with red and yellow tulips, fresh stems in water'},
        {key: 'peonies', label: 'Peony canvas', subject: 'a canvas of soft pink peonies with loose petals and green leaves'},
    ],
    styles: [
        {key: 'onestroke', label: 'One stroke', style: 'one-stroke acrylic, each petal a single loaded brushstroke of two blended colors', palette: 'fresh bright colors with green leaves', framing: 'Set on a white wooden table seen at eye level, a pale wall behind'},
        {key: 'dots', label: 'Dot accents', style: 'dot painting, simple flowers outlined with tiny raised dots of paint around each petal', palette: 'cheerful colors with white and gold dots', framing: 'Set on a sunny windowsill, a softly blurred garden beyond the glass'},
        {key: 'loose', label: 'Loose brushwork', style: 'loose impressionist brushwork with soft edges and visible strokes', palette: 'soft natural colors blended gently', framing: 'Set on a wooden shelf among small plants, seen slightly from above'},
    ],
    directions: ['sunflowers', 'lavender', 'on a mug'],
    directionHint: 'try: sunflowers',
    seo: {
        title: 'Flower Painting Ideas: Pot, Vase and Peony Canvas',
        metaDescription: 'Nine flower paintings: daisies on a terracotta pot, tulips on a glass vase and pink peonies on canvas, in three styles.',
        h1: 'Flower painting ideas: painted pot, painted vase, peony canvas',
        tagline: 'Three flower subjects, each painted in one-stroke, dot or loose brushwork.',
        intro: 'Flowers are painted on canvas and on the pots and vases that hold them. These nine show a terracotta pot painted with daisies, a glass vase painted with tulips and a canvas of soft pink peonies, each in one-stroke, dot or loose brushwork. They suit a painted pot on a windowsill or a vase for a table. Type a different flower or object, such as sunflowers or a mug, and the same three styles carry it.',
        useCases: [
            'a painted flower pot for a windowsill',
            'a painted vase for a dining table',
            'a canvas for a bedroom wall',
            'a first painting project with dots or one-stroke',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three flower subjects in three styles: a terracotta pot with white daisies, a glass vase with red and yellow tulips, and a canvas of soft pink peonies.'},
            {q: 'What is the difference between the three styles?', a: 'One-stroke paints each petal as a single loaded brushstroke in two blended colors. Dot painting outlines simple flowers with tiny raised dots. Loose brushwork uses soft edges and visible strokes.'},
            {q: 'Can I ask for other flowers or objects?', a: 'Yes. Type a direction such as sunflowers, lavender or on a mug, and the same three styles apply. The nine samples are the ones shown.'},
            {q: 'What shape are the pictures?', a: 'Each picture is tall, in portrait format.'},
        ],
    },
}
